import clsx from 'clsx';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import type {
  MetricHistoryEntry,
  ModelSchema,
  SignalVariant,
} from '@arcanum-hq/shared-types';
import { useSignalFieldResolver } from '@/hooks/use-signal-field-resolver';
import { excelSerialToDate } from '@/lib/format';
import { DealHeaderStrip } from './DealHeaderStrip';

// builder-ux-overhaul Phase 3 Session 2 (2026-05-22) — ModelHeaderStrip is
// now a thin wrapper around <DealHeaderStrip>. The inline header markup has
// been extracted into the shared strip (Session 1). This component resolves
// the Model-tab-specific props (subline derivation, HeroMetricsHeader fallback)
// and delegates all rendering to the shared strip.
//
// Testid decision (line-177 DECISION): the original `model-header-strip`
// testid is preserved on an outer <div> wrapper so existing test selectors
// and walkthrough runbook probes continue to resolve. `deal-header-strip` is
// emitted by <DealHeaderStrip>'s inner <header>; both are queryable.
// Session 5 updates any test selectors that need to migrate.

function formatSublineScalar(
  raw: unknown,
  kind: 'integer' | 'date' | 'string'
): string | null {
  if (raw == null || raw === '') return null;
  if (kind === 'integer' && typeof raw === 'number' && Number.isFinite(raw)) {
    return raw.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  if (kind === 'date' && typeof raw === 'number' && Number.isFinite(raw)) {
    return excelSerialToDate(raw);
  }
  if (typeof raw === 'string') return raw;
  return String(raw);
}

export interface ModelHeaderStripProps {
  conversationId: string;
  modelId: string;
  modelVersion?: string;
  signalVariant: SignalVariant | null;
  title: string;
  modelName?: string;
  metricHistory: Record<string, MetricHistoryEntry[]> | null;
  liveMetrics: Record<string, number>;
  modelSchema: ModelSchema | null;
  /** builder-ux-overhaul Phase 4 Session 2 — threaded to inner <DealHeaderStrip pillSlot>. */
  pillSlot?: ReactNode;
  className?: string;
}

export function ModelHeaderStrip({
  conversationId,
  modelId,
  modelVersion,
  signalVariant,
  title,
  modelName,
  metricHistory,
  liveMetrics,
  modelSchema,
  pillSlot,
  className,
}: ModelHeaderStripProps) {
  const resolver = useSignalFieldResolver({
    kind: 'conversation',
    conversationId,
  });

  const subline = useMemo(() => {
    if (!signalVariant) {
      // Variant-less fallback: show the model name (uppercased) so the row
      // still has a hierarchy under the title.
      return modelName ? modelName.toUpperCase() : 'MODEL';
    }
    if (!resolver.isReady)
      return signalVariant === 'operating-asset'
        ? 'OPERATING ASSET'
        : 'NEW DEVELOPMENT';
    const units = formatSublineScalar(
      resolver.resolve('snapshotUnits'),
      'integer'
    );
    const parts: string[] = [];
    if (units != null) parts.push(`${units} UNITS`);
    if (signalVariant === 'operating-asset') {
      const market = formatSublineScalar(
        resolver.resolve('snapshotAddress'),
        'string'
      );
      const acquired = formatSublineScalar(
        resolver.resolve('snapshotAcquisitionDate'),
        'date'
      );
      if (market != null) parts.push(market.toUpperCase());
      if (acquired != null) parts.push(`ACQUIRED ${acquired.toUpperCase()}`);
    } else {
      const market = formatSublineScalar(
        resolver.resolve('snapshotLocation'),
        'string'
      );
      const start = formatSublineScalar(
        resolver.resolve('snapshotProjectStart'),
        'date'
      );
      if (market != null) parts.push(market.toUpperCase());
      if (start != null) parts.push(`STARTS ${start.toUpperCase()}`);
    }
    parts.push(
      signalVariant === 'operating-asset'
        ? 'OPERATING ASSET'
        : 'NEW DEVELOPMENT'
    );
    return parts.join(' · ');
  }, [resolver, signalVariant, modelName]);

  return (
    // Outer wrapper preserves the legacy `model-header-strip` testid so
    // existing test selectors + walkthrough runbook probes continue to
    // resolve without change. `deal-header-strip` is queryable on the inner
    // <header> emitted by <DealHeaderStrip>. Session 5 migrates any selectors
    // that need to switch to the canonical testid.
    <div
      data-testid="model-header-strip"
      data-variant={signalVariant ?? 'none'}
      className={clsx('flex flex-col flex-shrink-0', className)}
    >
      <DealHeaderStrip
        conversationTitle={title}
        signalVariant={signalVariant}
        subline={subline}
        showHeroReturns
        heroContext={{ kind: 'conversation', conversationId }}
        heroModelId={modelId}
        heroModelVersion={modelVersion}
        heroMetricHistory={metricHistory}
        heroConversationId={conversationId}
        heroModelSchema={modelSchema}
        heroLiveMetrics={liveMetrics}
        pillSlot={pillSlot}
      />
    </div>
  );
}
